import {useCallback, useMemo, useState} from "react";
import useIsMounted from "../use-is-mounted";
import Api from "../../../core/services/api/api";
import {NotebookTagKeys, NotebookTagPages} from "../../../core/models/static/routes/notebook-tags";
import {routeFunctions, routes} from "../../../core/models/static/routes/app-routes";


/**
 * Fetches the information of the notebook tags and returns the page info by tags
 *
 * @param tags {Array<NoteTag> | undefined}
 * @returns {{
 *  pageInfo: ({title: string, url: string} | {title: string, action: function} | undefined),
 *  loading: boolean,
 *  labelTags: Array<NoteTag>
 * }}
 */
const useNotebookTagsInfo = (tags) => {
    const tagProps = tags;
    const [loading, setLoading] = useState(false);
    const isMounted = useIsMounted();

    /**
     * Opens the link in a new tab
     * @type {(function(*): void)}
     */
    const openLink = useCallback((url) => {
        window.open(window.location.origin + url, '_blank');
    }, [])

    /**
     * Fetches the customer admin user id by the customer code
     * @type {function(*): Promise<*>}
     */
    const getCustomerAdminCustomerId = useCallback(async (code) => {
        const response = await Api.getCustomerSimpleInfoByCode(code);
        if (!isMounted())
            return;
        return response?.data?.id;
    }, [isMounted])

    /**
     * Fetches the vendor id by the vendor code
     * @type {function(*): Promise<*>}
     */
    const getVendorIdByNo = useCallback(async (code) => {
        if (!code)
            return undefined;
        const response = await Api.listVendorsV2({
            keyword: code,
            pagination: {currentPage: 1, pageSize: 20},
            sorts: [],
        });
        if (!isMounted())
            return;
        const items = response?.data?.items ?? response?.data?.resultList ?? [];
        const match = items.find((v) => String(v.code).toLowerCase() === String(code).toLowerCase()) ?? items[0];
        return match?.id;
    }, [isMounted])

    /**
     * Fetches the responses of the given functions
     * @type {function(*): Promise<Awaited<unknown>[]>}
     */
    const getEntries = useCallback(async (functions) => {
        setLoading(true);
        const responses = await Promise.all(functions.map(f => f()));
        setLoading(false);
        return responses;
    }, [])

    const labelTags = useMemo(() => {
        return tagProps?.filter(e => e.isLabel);
    }, [tagProps])

    // TODO: add new pages here for notebook page info.
    const pageInfo = useMemo(() => {
        const tags = tagProps ?? [];
        const page = tags?.find(e => e.key === NotebookTagKeys.page);
        const query = tags?.find(e => e.key === NotebookTagKeys.query);
        const customerNo = tags?.find(e => e.key === NotebookTagKeys.customerNo);
        const section = tags.find(e => e.key === NotebookTagKeys.section);
        const partNo = tags.find(e => e.key === NotebookTagKeys.partNo);
        switch (page?.value) {
            case NotebookTagPages.operatorProfile:
                return {
                    title: 'Operator Profile',
                    url: routes.operatorProfile,
                }
            case NotebookTagPages.partnerProfile:
                return {
                    title: 'Partner Profile',
                    url: routes.partnerProfile,
                }
            case NotebookTagPages.printHub:
                return {
                    title: 'Print Hub',
                    url: routes.printing.printHub,
                }
            case NotebookTagPages.printLabels:
                return {
                    title: 'Print Labels',
                    url: routes.printing.printLabels,
                }
            case NotebookTagPages.printCatalogue:
                return {
                    title: 'Print Catalogue',
                    url: routes.printing.printCatalogue,
                }
            case NotebookTagPages.printBrochure:
                return {
                    title: 'Print Poster',
                    url: routes.printing.printBrochure,
                }
            case NotebookTagPages.printPackingList:
                return {
                    title: 'Print Packing List',
                    url: routes.printing.printPackingList,
                }
            case NotebookTagPages.notificationManagement:
                return {
                    title: 'Notification Management',
                    url: routes.notificationManagement,
                }
            case NotebookTagPages.lookupProductInformation:
                if (!partNo?.value)
                    return undefined;
                return {
                    title: 'Lookup Product Information',
                    url: routeFunctions.lookup.single(partNo?.value)
                }
            case NotebookTagPages.lookupCompareList:
                return {
                    title: 'Lookup Compare List',
                    url: routes.lookup.compare,
                }
            case NotebookTagPages.warrantyAndReturnRequests:
                return {
                    title: 'Warranty And Return Requests',
                    url: routes.warrantyAndReturn.requests,
                }
            case NotebookTagPages.warrantyAndReturnInformation:
                if (!query?.value)
                    return undefined;
                return {
                    title: 'Warranty And Return Information',
                    url: routeFunctions.warrantyAndReturn.single(query?.value),
                }
            case NotebookTagPages.warrantyAndReturnSettingsStatuses:
                return {
                    title: 'Warranty And Return Settings Statuses',
                    url: routes.warrantyAndReturn.settings.statuses,
                }
            case NotebookTagPages.warrantyAndReturnSettingsProcess:
                return {
                    title: 'Warranty And Return Settings Process',
                    url: routes.warrantyAndReturn.settings.process,
                }
            case NotebookTagPages.drivers:
                return {
                    title: 'Drivers',
                    url: routes.drivers,
                }
            case NotebookTagPages.dashboard:
                return {
                    title: 'Dashboard',
                    url: routes.analytics.systemDashboards,
                }
            case NotebookTagPages.reports:
                return {
                    title: 'Reports',
                    url: routes.analytics.systemReports,
                }
            case NotebookTagPages.lookupAnalytics:
                return {
                    title: 'Lookup Analytics',
                    url: routes.analytics.lookup.base,
                }
            case NotebookTagPages.contentBlogList:
                return {
                    title: 'Content Blog List',
                    url: routes.content.blogs.list,
                }
            case NotebookTagPages.contentBlogInformation:
                if (!query?.value)
                    return undefined;
                return {
                    title: 'Content Blog Information',
                    url: routeFunctions.content.blogs.single(query?.value),
                }
            case NotebookTagPages.contentVideosList:
                return {
                    title: 'Content Videos List',
                    url: routes.content.videos.list,
                }
            case NotebookTagPages.contentVideoInformation:
                if (!query?.value)
                    return undefined;
                return {
                    title: 'Content Video Information',
                    url: routeFunctions.content.videos.single(query?.value),
                }
            case NotebookTagPages.customersList:
                return {
                    title: 'Customers List',
                    url: routes.customers.list,
                }
            case NotebookTagPages.customersMembersActive:
                return {
                    title: 'Customers Members Active',
                    url: routes.customers.members.active,
                }
            case NotebookTagPages.customersMembersInactive:
                return {
                    title: 'Customers Members Inactive',
                    url: routes.customers.members.inactive,
                }
            case NotebookTagPages.customersMembersPassive:
                return {
                    title: 'Customers Members Passive',
                    url: routes.customers.members.passive,
                }
            case NotebookTagPages.operatorsList:
                return {
                    title: 'Operators List',
                    url: routes.operators.list,
                }
            case NotebookTagPages.operatorsGroups:
                return {
                    title: 'Operators Groups',
                    url: routes.operators.groups.base,
                }
            case NotebookTagPages.operatorsGroupAccessLevels:
                if (!query?.value)
                    return undefined;
                return {
                    title: 'Operators Group Access Levels',
                    url: routeFunctions.operators.groups.single(query?.value),
                }
            case NotebookTagPages.operatorsDashboards:
                if (!query?.value)
                    return undefined;
                return {
                    title: 'Operators Dashboards',
                    url: query?.value
                        ? routeFunctions.operators.dashboards.single(query?.value)
                        : routes.operators.dashboards.base,
                }
            case NotebookTagPages.salesQuotes:
                return {
                    title: 'Sales Quotes',
                    url: routes.sales.quotes,
                }
            case NotebookTagPages.salesSalesOrders:
                return {
                    title: 'Sales Sales Orders',
                    url: routes.sales.quotes,
                }
            case NotebookTagPages.salesBackOrders:
                return {
                    title: 'Sales Back Orders',
                    url: routes.sales.backOrder,
                }
            case NotebookTagPages.salesInvoices:
                return {
                    title: 'Sales Invoices',
                    url: routes.sales.invoices,
                }
            case NotebookTagPages.salesCredits:
                return {
                    title: 'Sales Credits',
                    url: routes.sales.credits,
                }
            case NotebookTagPages.salesPersonDashboard:
                return {
                    title: 'Sales Person Dashboard',
                    url: routes.salesPerson.dashboard,
                }
            case NotebookTagPages.accountingInvoices:
                return {
                    title: 'Accounting Invoices',
                    url: routes.accounting.invoices,
                }
            case NotebookTagPages.accountingOnlinePaymentTransactions:
                return {
                    title: 'Accounting Online Payment Transactions',
                    url: routes.accounting.onlinePaymentTransactions,
                }
            case NotebookTagPages.accountingAccountReceivables:
                return {
                    title: 'Accounting Account Receivables',
                    url: routes.accounting.accountReceivable,
                }
            case NotebookTagPages.userNotificationsList:
                return {
                    title: 'User Notifications List',
                    url: routes.userNotifications.list,
                }
            case NotebookTagPages.userNotificationsAccessLevels:
                return {
                    title: 'User Notifications Access Levels',
                    url: routes.userNotifications.accessLevels,
                }
            case NotebookTagPages.userNotificationsSubscriptions:
                return {
                    title: 'User Notifications Subscriptions',
                    url: routes.userNotifications.subscriptions,
                }
            case NotebookTagPages.userPlatformList:
                return {
                    title: 'User Platform List',
                    url: routes.userPlatform.list,
                }
            case NotebookTagPages.userPlatformLiveHomepages:
                return {
                    title: 'User Platform Live Homepages',
                    url: routes.userPlatform.liveHomepages,
                }
            case NotebookTagPages.userPlatformPhoneHomepage:
                if (!query?.value)
                    return undefined;
                return {
                    title: 'User Platform Phone Homepage',
                    url: routeFunctions.userPlatform.phoneHomepage(query?.value),
                }
            case NotebookTagPages.userPlatformWebHomepage:
                if (!query?.value)
                    return undefined;
                return {
                    title: 'User Platform Web Homepage',
                    url: routeFunctions.userPlatform.webHomepage(query?.value),
                }
            case NotebookTagPages.userPlatformThemeManagementPhone:
                return {
                    title: 'User Platform Theme Management Phone',
                    url: routes.userPlatform.themeManagement.phone,
                }
            case NotebookTagPages.userPlatformThemeManagementWeb:
                return {
                    title: 'User Platform Theme Management Web',
                    url: routes.userPlatform.themeManagement.web,
                }
            case NotebookTagPages.userPlatformBannerManagement:
                return {
                    title: 'User Platform Banner Management',
                    url: routes.userPlatform.bannerManagement,
                }
            case NotebookTagPages.userPlatformContentManagement:
                return {
                    title: 'User Platform Content Management',
                    url: routes.userPlatform.contentManagement,
                }
            case NotebookTagPages.userPlatformDynamicPagesList:
                return {
                    title: 'Dynamic Pages List',
                    url: routes.userPlatform.dynamicPages.list,
                }
            case NotebookTagPages.userPlatformDynamicPageSingle:
                if (!query?.value)
                    return undefined;
                return {
                    title: 'Dynamic Page',
                    url: routeFunctions.userPlatform.dynamicPages.single(query?.value),
                }
            case NotebookTagPages.settingsOrganization:
                return {
                    title: 'Settings Organization',
                    url: routes.settings.organization,
                }
            case NotebookTagPages.settingsPersonal:
                return {
                    title: 'Settings Personal',
                    url: routes.settings.personal,
                }
            case NotebookTagPages.giftCardsList:
                return {
                    title: 'Gift Cards List',
                    url: routes.settings.giftCards.list,
                }
            case NotebookTagPages.giftCardsCategories:
                return {
                    title: 'Gift Cards Categories',
                    url: routes.settings.giftCards.categories,
                }
            case NotebookTagPages.priceMatrixList:
                return {
                    title: 'Price Matrix List',
                    url: routes.settings.priceMatrix.list,
                }
            case NotebookTagPages.priceMatrixCustomers:
                return {
                    title: 'Price Matrix Customers',
                    url: routes.settings.priceMatrix.customers,
                }
            case NotebookTagPages.settingsExport:
                return {
                    title: 'Settings Export',
                    url: routes.settings.export,
                }
            case NotebookTagPages.settingsImport:
                return {
                    title: 'Settings Import',
                    url: routes.settings.import,
                }
            case NotebookTagPages.settingsPartnersManagement:
                return {
                    title: 'Settings Partners Management',
                    url: routes.settings.partnersManagement,
                }
            case NotebookTagPages.promotionsList:
                return {
                    title: 'Promotions List',
                    url: routes.sales.promotions.list,
                }
            case NotebookTagPages.promotionInformation:
                if (!query?.value)
                    return undefined;
                return {
                    title: 'Promotion Information',
                    url: routeFunctions.settings.promotion.single(query?.value),
                }
            case NotebookTagPages.regions:
                return {
                    title: 'Regions',
                    url: routes.settings.regions,
                }
            case NotebookTagPages.partnership:
                return {
                    title: 'Partnership',
                    url: routes.settings.partnership,
                }
            case NotebookTagPages.damageControlMissingProducts:
                return {
                    title: 'Damage Control Missing Products',
                    url: routes.damageControl.missingProducts,
                }
            case NotebookTagPages.damageControlTransactions:
                return {
                    title: 'Damage Control Transactions',
                    url: routes.damageControl.transactions,
                }
            case NotebookTagPages.productsList:
                return {
                    title: 'Products List',
                    url: routes.products.list,
                }
            case NotebookTagPages.productInformation:
                if (!query?.value || !section?.value)
                    return undefined;
                return {
                    title: 'Product Information',
                    url: routeFunctions.products.single(query?.value, section?.value),
                }
            case NotebookTagPages.productsTagsList:
                return {
                    title: 'Products Tags List',
                    url: routes.products.tags.list,
                }
            case NotebookTagPages.productsTagInformation:
                return {
                    title: 'Products Tag Information',
                    url: routeFunctions.products.tags.single(query?.value),
                }
            case NotebookTagPages.productsRelationTypes:
                return {
                    title: 'Products Relation Types',
                    url: routes.products.relationTypes,
                }
            case NotebookTagPages.productsManageSuggestions:
                return {
                    title: 'Products Manage Suggestions',
                    url: routes.products.manageSuggestions,
                }
            case NotebookTagPages.productsFastConnection:
                return {
                    title: 'Products Fast Connection',
                    url: routes.products.productFastConnection,
                }
            case NotebookTagPages.bulkPriceChangeDashboard:
                return {
                    title: 'Bulk Price Change Dashboard',
                    url: routes.products.bulkPriceChange.dashboard,
                }
            case NotebookTagPages.bulkPriceChangeReport:
                if (!query?.value)
                    return undefined;
                return {
                    title: 'Bulk Price Change Report',
                    url: routeFunctions.products.bulkPriceChange.report(query?.value),
                }
            case NotebookTagPages.bulkPriceChange:
                if (!query?.value)
                    return undefined;
                return {
                    title: 'Bulk Price Change',
                    url: routeFunctions.products.bulkPriceChange.newBulkPrice(query?.value),
                }
            case NotebookTagPages.inventoryAdjustmentList:
                return {
                    title: 'Inventory Adjustment List',
                    url: routes.inventory.inventoryAdjustment.list,
                }
            case NotebookTagPages.inventoryAdjustmentItems:
                return {
                    title: 'Inventory Adjustment Items',
                    url: routes.inventory.inventoryAdjustment.items,
                }
            case NotebookTagPages.inventoryAdjustmentReport:
                return {
                    title: 'Inventory Adjustment Report',
                    url: routes.inventory.inventoryAdjustment.report,
                }
            case NotebookTagPages.inventoryAdjustmentInformation:
                if (!query?.value)
                    return undefined;
                return {
                    title: 'Inventory Adjustment Information',
                    url: routeFunctions.products.inventoryAdjustment.single(query?.value),
                }
            case NotebookTagPages.inventoryAdjustmentBulkAvailabilityChange:
                return {
                    title: 'Inventory Adjustment Bulk Availability Change',
                    url: routes.products.inventoryAdjustment.bulkAvailabilityChange,
                }
            case NotebookTagPages.inventoryTransferBatches:
                return {
                    title: 'Inventory Transfer Batches',
                    url: routes.products.inventoryTransfer.batches,
                }
            case NotebookTagPages.inventoryTransferBatchInformation:
                if (!query?.value)
                    return undefined;
                return {
                    title: 'Inventory Transfer Batch Information',
                    url: routeFunctions.products.inventoryTransfer.batchInformation(query?.value),
                }
            case NotebookTagPages.inventoryTransferPostTransfer:
                if (!query?.value)
                    return undefined;
                return {
                    title: 'Inventory Transfer Post Transfer',
                    url: routeFunctions.products.inventoryTransfer.postTransfer(query?.value),
                }
            case NotebookTagPages.inventoryTransferReturnInformation:
                if (!query?.value)
                    return undefined;
                return {
                    title: 'Inventory Transfer Return Information',
                    url: routeFunctions.products.inventoryTransfer.returnInformation(...(query?.value?.split('-') ?? [])),
                }
            case NotebookTagPages.inventoryTransferHistoryList:
                return {
                    title: 'Inventory Transfer History List',
                    url: routes.products.inventoryTransfer.historyList,
                }
            case NotebookTagPages.inventoryTransferHistoryItems:
                return {
                    title: 'Inventory Transfer History Items',
                    url: routes.products.inventoryTransfer.historyItems,
                }
            case NotebookTagPages.inventoryTransferHistoryInformation:
                if (!query?.value)
                    return undefined;
                return {
                    title: 'Inventory Transfer History Information',
                    url: routeFunctions.products.inventoryTransfer.historyInformation(query?.value),
                }
            case NotebookTagPages.shippingFeesList:
                return {
                    title: 'Shipping Fees List',
                    url: routes.shippingFees.list,
                }
            case NotebookTagPages.shippingFeesSettings:
                return {
                    title: 'Shipping Fees Settings',
                    url: routes.shippingFees.settings,
                }
            case NotebookTagPages.shippingFeesCompanies:
                return {
                    title: 'Shipping Fees Companies',
                    url: routes.shippingFees.companies,
                }
            case NotebookTagPages.shippingFeesReports:
                return {
                    title: 'Shipping Fees Reports',
                    url: routes.shippingFees.reports,
                }
            case NotebookTagPages.customerProfile:
                if (!customerNo?.value)
                    return undefined;
                return {
                    title: 'Customer Profile',
                    action: () =>
                        getEntries([() => getCustomerAdminCustomerId(customerNo?.value)])
                            .then(([customerId]) => {
                                if (customerId) {
                                    openLink(routeFunctions.customer.generalInformation.profile(customerId))
                                }
                            }),
                }
            case NotebookTagPages.customerAddresses:
                if (!customerNo?.value)
                    return undefined;
                return {
                    title: 'Customer Addresses',
                    action: () =>
                        getEntries([() => getCustomerAdminCustomerId(customerNo?.value)])
                            .then(([customerId]) => {
                                if (customerId) {
                                    openLink(routeFunctions.customer.generalInformation.addresses(customerId))
                                }
                            }),
                }
            case NotebookTagPages.customerLoginLogs:
                if (!customerNo?.value)
                    return undefined;
                return {
                    title: 'Customer Login Logs',
                    action: () =>
                        getEntries([() => getCustomerAdminCustomerId(customerNo?.value)])
                            .then(([customerId]) => {
                                if (customerId) {
                                    openLink(routeFunctions.customer.generalInformation.loginLogs(customerId))
                                }
                            }),
                }
            case NotebookTagPages.customerPromotions:
                if (!customerNo?.value)
                    return undefined;
                return {
                    title: 'Customer Promotions',
                    action: () =>
                        getEntries([() => getCustomerAdminCustomerId(customerNo?.value)])
                            .then(([customerId]) => {
                                if (customerId) {
                                    openLink(routeFunctions.customer.generalInformation.promotions(customerId))
                                }
                            }),
                }
            case NotebookTagPages.customerNotes:
                if (!customerNo?.value)
                    return undefined;
                return {
                    title: 'Customer Notes',
                    action: () =>
                        getEntries([() => getCustomerAdminCustomerId(customerNo?.value)])
                            .then(([customerId]) => {
                                if (customerId) {
                                    openLink(routeFunctions.customer.notes(customerId))
                                }
                            }),
                }
            case NotebookTagPages.customerPriceMatrix:
                if (!customerNo?.value)
                    return undefined;
                return {
                    title: 'Customer Price Matrix',
                    action: () =>
                        getEntries([() => getCustomerAdminCustomerId(customerNo?.value)])
                            .then(([customerId]) => {
                                if (customerId) {
                                    openLink(routeFunctions.customer.priceMatrix(customerId))
                                }
                            }),
                }
            case NotebookTagPages.customerAccountingAvailableCredits:
                if (!customerNo?.value)
                    return undefined;
                return {
                    title: 'Customer Accounting Available Credits',
                    action: () =>
                        getEntries([() => getCustomerAdminCustomerId(customerNo?.value)])
                            .then(([customerId]) => {
                                if (customerId) {
                                    openLink(routeFunctions.customer.accounting.availableCredits(customerId))
                                }
                            }),
                }
            case NotebookTagPages.customerSalesQuotes:
                if (!customerNo?.value)
                    return undefined;
                return {
                    title: 'Customer Sales Quotes',
                    action: () =>
                        getEntries([() => getCustomerAdminCustomerId(customerNo?.value)])
                            .then(([customerId]) => {
                                if (customerId) {
                                    openLink(routeFunctions.customer.sales.quotes.base(customerId))
                                }
                            }),
                }
            case NotebookTagPages.customerSalesQuoteInformation:
                if (!customerNo?.value || !query?.value)
                    return undefined;
                return {
                    title: 'Customer Sales Quote Information',
                    action: () =>
                        getEntries([() => getCustomerAdminCustomerId(customerNo?.value)])
                            .then(([customerId]) => {
                                if (customerId) {
                                    openLink(routeFunctions.customer.sales.quotes.single(customerId, query?.value))
                                }
                            }),
                }
            case NotebookTagPages.customerSalesSalesOrders:
                if (!customerNo?.value)
                    return undefined;
                return {
                    title: 'Customer Sales Sales-Orders',
                    action: () =>
                        getEntries([() => getCustomerAdminCustomerId(customerNo?.value)])
                            .then(([customerId]) => {
                                if (customerId) {
                                    openLink(routeFunctions.customer.sales.salesOrder.base(customerId))
                                }
                            }),
                }
            case NotebookTagPages.customerSalesSalesOrderInformation:
                if (!customerNo?.value || !query?.value)
                    return undefined;
                return {
                    title: 'Customer Sales Sales-Order Information',
                    action: () =>
                        getEntries([() => getCustomerAdminCustomerId(customerNo?.value)])
                            .then(([customerId]) => {
                                if (customerId) {
                                    openLink(routeFunctions.customer.sales.salesOrder.single(customerId, query?.value))
                                }
                            }),
                }
            case NotebookTagPages.customerSalesBackOrders:
                if (!customerNo?.value)
                    return undefined;
                return {
                    title: 'Customer Sales Back-Orders',
                    action: () =>
                        getEntries([() => getCustomerAdminCustomerId(customerNo?.value)])
                            .then(([customerId]) => {
                                if (customerId) {
                                    openLink(routeFunctions.customer.sales.backOrder.base(customerId))
                                }
                            }),
                }
            case NotebookTagPages.customerSalesBackOrderInformation:
                if (!customerNo?.value || !query?.value)
                    return undefined;
                return {
                    title: 'Customer Sales Back-Order Information',
                    action: () =>
                        getEntries([() => getCustomerAdminCustomerId(customerNo?.value)])
                            .then(([customerId]) => {
                                if (customerId) {
                                    openLink(routeFunctions.customer.sales.backOrder.single(customerId, query?.value))
                                }
                            }),
                }
            case NotebookTagPages.customerSalesInvoices:
                if (!customerNo?.value)
                    return undefined;
                return {
                    title: 'Customer Sales Invoices',
                    action: () =>
                        getEntries([() => getCustomerAdminCustomerId(customerNo?.value)])
                            .then(([customerId]) => {
                                if (customerId) {
                                    openLink(routeFunctions.customer.sales.invoices.base(customerId))
                                }
                            }),
                }
            case NotebookTagPages.customerSalesInvoiceInformation:
                if (!customerNo?.value || !query?.value)
                    return undefined;
                return {
                    title: 'Customer Sales Invoice Information',
                    action: () =>
                        getEntries([() => getCustomerAdminCustomerId(customerNo?.value)])
                            .then(([customerId]) => {
                                if (customerId) {
                                    openLink(routeFunctions.customer.sales.invoices.single(customerId, query?.value))
                                }
                            }),
                }
            case NotebookTagPages.customerSalesCredits:
                if (!customerNo?.value)
                    return undefined;
                return {
                    title: 'Customer Sales Credits',
                    action: () =>
                        getEntries([() => getCustomerAdminCustomerId(customerNo?.value)])
                            .then(([customerId]) => {
                                if (customerId) {
                                    openLink(routeFunctions.customer.sales.credits.base(customerId))
                                }
                            }),
                }
            case NotebookTagPages.customerSalesCreditInformation:
                if (!customerNo?.value || !query?.value)
                    return undefined;
                return {
                    title: 'Customer Sales Credit Information',
                    action: () =>
                        getEntries([() => getCustomerAdminCustomerId(customerNo?.value)])
                            .then(([customerId]) => {
                                if (customerId) {
                                    openLink(routeFunctions.customer.sales.credits.single(customerId, query?.value))
                                }
                            }),
                }
            case NotebookTagPages.customerSalesShoppingCart:
                if (!customerNo?.value)
                    return undefined;
                return {
                    title: 'Customer Sales Shopping Cart',
                    action: () =>
                        getEntries([() => getCustomerAdminCustomerId(customerNo?.value)])
                            .then(([customerId]) => {
                                if (customerId) {
                                    openLink(routeFunctions.customer.sales.shoppingCart(customerId))
                                }
                            }),
                }
            case NotebookTagPages.customerSalesActiveCards:
                if (!customerNo?.value)
                    return undefined;
                return {
                    title: 'Customer Sales Active Cards',
                    action: () =>
                        getEntries([() => getCustomerAdminCustomerId(customerNo?.value)])
                            .then(([customerId]) => {
                                if (customerId) {
                                    openLink(routeFunctions.customer.generalInformation.paymentMethods(customerId))
                                }
                            }),
                }
            case NotebookTagPages.customerCardSummaryCreditCards:
                if (!customerNo?.value)
                    return undefined;
                return {
                    title: 'Customer Card Summary Credit Cards',
                    action: () =>
                        getEntries([() => getCustomerAdminCustomerId(customerNo?.value)])
                            .then(([customerId]) => {
                                if (customerId) {
                                    openLink(routeFunctions.customer.cardSummary.creditCards(customerId))
                                }
                            }),
                }
            case NotebookTagPages.customerCardSummaryGiftCards:
                if (!customerNo?.value)
                    return undefined;
                return {
                    title: 'Customer Card Summary Gift Cards',
                    action: () =>
                        getEntries([() => getCustomerAdminCustomerId(customerNo?.value)])
                            .then(([customerId]) => {
                                if (customerId) {
                                    openLink(routeFunctions.customer.cardSummary.giftCards(customerId))
                                }
                            }),
                }
            case NotebookTagPages.customerShippingFeesList:
                if (!customerNo?.value)
                    return undefined;
                return {
                    title: 'Customer Shipping Fees List',
                    action: () =>
                        getEntries([() => getCustomerAdminCustomerId(customerNo?.value)])
                            .then(([customerId]) => {
                                if (customerId) {
                                    openLink(routeFunctions.customer.shippingFees.list(customerId))
                                }
                            }),
                }
            case NotebookTagPages.customerShippingFeesSettings:
                if (!customerNo?.value)
                    return undefined;
                return {
                    title: 'Customer Shipping Fees Settings',
                    action: () =>
                        getEntries([() => getCustomerAdminCustomerId(customerNo?.value)])
                            .then(([customerId]) => {
                                if (customerId) {
                                    openLink(routeFunctions.customer.shippingFees.settings(customerId))
                                }
                            }),
                }
            case NotebookTagPages.surveyList:
                return {
                    title: 'Survey List',
                    url: routes.userPlatform.surveys.list,
                }
            case NotebookTagPages.surveyPreview:
                if (!query?.value)
                    return undefined;
                return {
                    title: 'Survey Preview',
                    url: routeFunctions.userPlatform.surveys.single(query?.value),
                }
            case NotebookTagPages.purchaseList:
                return {
                    title: 'Purchase List',
                    url: routes.purchase.orders,
                }
            case NotebookTagPages.vendorsList:
                return {
                    title: 'Vendors List',
                    url: routes.vendors,
                }
            case NotebookTagPages.vendorProfile:
            case NotebookTagPages.vendorAccountInfo:
                if (!query?.value)
                    return undefined;
                return {
                    title: 'Vendor Account Info',
                    action: () =>
                        getEntries([() => getVendorIdByNo(query?.value)])
                            .then(([vendorId]) => {
                                if (vendorId) {
                                    openLink(routeFunctions.vendor.accountInfo(vendorId))
                                }
                            }),
                }
            case NotebookTagPages.vendorAddresses:
                if (!query?.value)
                    return undefined;
                return {
                    title: 'Vendor Account Info',
                    action: () =>
                        getEntries([() => getVendorIdByNo(query?.value)])
                            .then(([vendorId]) => {
                                if (vendorId) {
                                    openLink(routeFunctions.vendor.accountInfo(vendorId))
                                }
                            }),
                }
            case NotebookTagPages.vendorNotes:
                if (!query?.value)
                    return undefined;
                return {
                    title: 'Vendor Notes',
                    action: () =>
                        getEntries([() => getVendorIdByNo(query?.value)])
                            .then(([vendorId]) => {
                                if (vendorId) {
                                    openLink(routeFunctions.vendor.notes(vendorId))
                                }
                            }),
                }
            case NotebookTagPages.vendorPurchaseList:
            case NotebookTagPages.vendorPurchaseOrders:
                if (!query?.value)
                    return undefined;
                return {
                    title: 'Vendor Purchase Orders',
                    action: () =>
                        getEntries([() => getVendorIdByNo(query?.value)])
                            .then(([vendorId]) => {
                                if (vendorId) {
                                    openLink(routeFunctions.vendor.purchases.orders.base(vendorId))
                                }
                            }),
                }
            case NotebookTagPages.vendorPurchaseHistory:
                if (!query?.value)
                    return undefined;
                return {
                    title: 'Vendor Purchase History',
                    action: () =>
                        getEntries([() => getVendorIdByNo(query?.value)])
                            .then(([vendorId]) => {
                                if (vendorId) {
                                    openLink(routeFunctions.vendor.purchases.history.base(vendorId))
                                }
                            }),
                }
            case NotebookTagPages.vendorPurchaseOrderSingle:
            case NotebookTagPages.vendorPurchaseHistorySingle:
                // query.value is "vendorNo:entityId"
                if (!query?.value)
                    return undefined;
                return {
                    title: page?.value === NotebookTagPages.vendorPurchaseHistorySingle
                        ? 'Vendor Purchase History'
                        : 'Vendor Purchase Order',
                    action: () => {
                        const [vendorNo, entityId] = String(query.value).split(':');
                        return getEntries([() => getVendorIdByNo(vendorNo)])
                            .then(([vendorId]) => {
                                if (!vendorId || !entityId) return;
                                if (page?.value === NotebookTagPages.vendorPurchaseHistorySingle) {
                                    openLink(routeFunctions.vendor.purchases.history.single(vendorId, entityId));
                                } else {
                                    openLink(routeFunctions.vendor.purchases.orders.single(vendorId, entityId));
                                }
                            });
                    },
                }
            case NotebookTagPages.notebook:
                return {
                    title: 'Notebook',
                    url: routes.notebook,
                }
            case NotebookTagPages.ncrList:
                return {
                    title: 'NCR List',
                    url: routes.ncr.list,
                }
            case NotebookTagPages.ncrNotes:
                return {
                    title: 'NCR Notes',
                    url: routes.ncr.notes,
                }
            case NotebookTagPages.ncrTasks:
                return {
                    title: 'NCR Tasks',
                    url: routes.ncr.tasks,
                }
            case NotebookTagPages.ncrInformation:
                if (!query?.value)
                    return undefined;
                return {
                    title: 'NCR Information',
                    url: routeFunctions.ncr.single(query?.value),
                }
            case NotebookTagPages.ncrSettings:
                if (!query?.value)
                    return undefined;
                return {
                    title: 'NCR Settings',
                    url: routeFunctions.ncr.settings(query?.value),
                }
            case NotebookTagPages.formsManagementList:
                return {
                    title: 'Forms list',
                    url: routes.forms.list,
                }
            case NotebookTagPages.formsManagementResponses:
                return {
                    title: 'Form responses',
                    url: routes.forms.responses,
                }
            case NotebookTagPages.customerFormResponses:
                if (!customerNo?.value)
                    return undefined;
                return {
                    title: 'Customer form responses',
                    url: routeFunctions.customer.forms.responses(Number(customerNo.value)),
                }
            case NotebookTagPages.productsCategoryManagement:
                return {
                    title: 'Products Category Management',
                    url: routes.products.categoryManagement,
                }
            case NotebookTagPages.settingsIndustryManagement:
                return {
                    title: 'Settings Industry Management',
                    url: routes.settings.industries,
                }
            case NotebookTagPages.productsCodeTypes:
                return {
                    title: 'Products Code Types',
                    url: routes.products.codeTypes,
                }
            case NotebookTagPages.productsShareRules:
                return {
                    title: 'Products Share Rules',
                    url: routes.products.shareRules,
                }
            case NotebookTagPages.manageProductProperties:
                return {
                    title: 'Manage Product Properties',
                    url: routes.products.manageProperties,
                }
            case NotebookTagPages.lookupNexusSearch:
                return {
                    title: 'Lookup Nexus Search',
                    url: routes.lookup.search,
                }
            case NotebookTagPages.competitorPriceReport:
                return {
                    title: 'Competitor Price Report',
                    url: routes.lookup.competitorPriceReport,
                }
            case NotebookTagPages.taxesList:
                return {
                    title: 'Taxes List',
                    url: routes.settings.taxes.list,
                }
            case NotebookTagPages.priceLevelsConfig:
                return {
                    title: 'Price Levels Configurations',
                    url: routes.settings.priceLevels.config,
                }
            case NotebookTagPages.taxesRegions:
                return {
                    title: 'Taxes Regions',
                    url: routes.settings.taxes.regions,
                }
            case NotebookTagPages.salesPersonAccessLevels:
                return {
                    title: 'Sales Person Access Levels',
                    url: routes.salesPerson.accessLevels,
                }
            case NotebookTagPages.territories:
                return {
                    title: 'Territories',
                    url: routes.settings.territories,
                }
            default:
                return undefined;
        }
    }, [tagProps, getEntries, getCustomerAdminCustomerId, openLink, getVendorIdByNo])

    return {pageInfo, loading, labelTags};
}

export default useNotebookTagsInfo;
